// Minimal history router: the path is the state, links push entries.
import { useSyncExternalStore } from 'react';

export type Route =
  | { name: 'today' }
  | { name: 'history' }
  | { name: 'habits' }
  | { name: 'habit'; id: string }
  | { name: 'account' }
  | { name: 'reset-password' }
  | { name: 'not-found' };

export function parseRoute(pathname: string): Route {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return { name: 'today' };
  if (path === '/verlauf') return { name: 'history' };
  if (path === '/habits') return { name: 'habits' };
  if (path === '/konto') return { name: 'account' };
  if (path === '/reset-password') return { name: 'reset-password' };
  const habit = /^\/habit\/([0-9a-f-]{36})$/i.exec(path);
  if (habit) return { name: 'habit', id: habit[1]! };
  return { name: 'not-found' };
}

const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};
window.addEventListener('popstate', notify);

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getLocation = () => window.location.pathname + window.location.search;

export function useLocation(): string {
  return useSyncExternalStore(subscribe, getLocation);
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (to === getLocation()) return;
  const state = { index: (history.state?.index ?? 0) + (options.replace ? 0 : 1) };
  if (options.replace) history.replaceState(state, '', to);
  else history.pushState(state, '', to);
  window.scrollTo({ top: 0 });
  notify();
}

/** Back within the app when there is an entry to go back to, else to `fallback`. */
export function goBack(fallback = '/'): void {
  if ((history.state?.index ?? 0) > 0) history.back();
  else navigate(fallback, { replace: true });
}

export const paths = {
  today: '/',
  history: '/verlauf',
  habits: '/habits',
  account: '/konto',
  habit: (id: string) => `/habit/${id}`,
};
